/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useState } from "react";
import { BEM, useTranslate } from "@adapptio/lib-components";
import { isDefined, isFunction } from "@adapptio/lib-shared";
import { IEditorApiIntegrationResponse, RESOURCE_TYPES } from "@adapptio/lib-core";
import { Icon, ICON_SIZE } from "../../../../ui/components/Icon";
import { Form } from "../../../../ui/components/Form";
import { FieldText } from "../../../../ui/components/Fields/FieldText";
import { ACTION_BUTTON_STYLE, ActionButton } from "../../../../ui/components/ActionButton";
import { Panel } from "../../../../ui/components/Panel";
import { FieldLabel } from "../../../../ui/components/Fields/FieldLabel";
import { IResourceListProps, IResourceNode, ResourceList } from "../../../../ui/components/ResourceList";
import { useDeepCompareMemo } from "use-deep-compare";
import { normalizeResourcePath } from "../../../../resources/ResourceHelpers";
import { useEditorMainContext } from "../../../../ui/contexts/EditorMainContext";
import { IntegrationEditorCreate } from "../IntegrationEditorCreate";

export interface IIntegrationEditorCreateDetailsProps {
	className?: string;
	selectedIntegration?: IEditorApiIntegrationResponse;
	onSubmitConfig?: (integration: IEditorApiIntegrationResponse, name: string, basePath: string) => void;
	creating?: boolean;
	view: IntegrationEditorCreate;
}

const allowedResourceTypes: RESOURCE_TYPES[] = [ RESOURCE_TYPES.DIRECTORY ];

export const IntegrationEditorCreateDetails: React.FC<IIntegrationEditorCreateDetailsProps> = ({
	className,
	selectedIntegration,
	onSubmitConfig,
	creating,
	view
}) => {
	const t = useTranslate();

	const initBasePath = useMemo(() => view.getBasePath(), []);
	const hideDirSelector = isDefined(initBasePath);

	const [ selectedParentNode, setSelectedParentNode ] = useState<IResourceNode>(null);

	const selectResource = useCallback<IResourceListProps["onSelect"]>((selectedNode) => {
		setSelectedParentNode(selectedNode);
	}, []);

	const basePath = useDeepCompareMemo(() => {
		if (isDefined(initBasePath)) {
			return initBasePath;
		}

		if (selectedParentNode && !selectedParentNode.isRoot) {
			return normalizeResourcePath(selectedParentNode.basePath, selectedParentNode.name);
		}
		return "";
	}, [ selectedParentNode ]);

	const baseClass = BEM("edt-integration-editor-create-details");

	const [ name, setName ] = useState("");

	const submitConfig = useCallback(() => {
		if (isFunction(onSubmitConfig)) {
			onSubmitConfig(selectedIntegration, name, basePath);
		}
	}, [ selectedIntegration, name, onSubmitConfig, basePath ]);

	if (className) {
		baseClass.add(className);
	}

	const { appInfo } = useEditorMainContext();

	return (
		<Panel className={baseClass.toClassName()}>
			{selectedIntegration ? (
				<div className={baseClass.element("content").toClassName()}>
					<div className={baseClass.element("info").toClassName()}>
						<div className={baseClass.element("info-icon").toClassName()}>
							<Icon name={selectedIntegration.icon} size={ICON_SIZE.LARGE} />
						</div>
						<div className={baseClass.element("info-text").toClassName()}>
							<div className={baseClass.element("info-text-primary").toClassName()}>
								{selectedIntegration.label}
							</div>
							<div className={baseClass.element("info-text-secondary").toClassName()}>
								{selectedIntegration.description}
							</div>
						</div>
					</div>

					<Form className={baseClass.element("config").toClassName()} onSubmit={submitConfig}>
						<fieldset disabled={creating} className={baseClass.element("row").toClassName()}>
							<FieldLabel>Name:</FieldLabel>
							<FieldText value={name} onChange={setName} grow autoFocus />
						</fieldset>

						{!hideDirSelector && (
							<div className={baseClass.element("field-row-resource-list").toClassName()}>
								<ResourceList
									onSelect={selectResource}
									labelSearch="Select target directory"
									allowedResourceTypes={allowedResourceTypes}
									selectedId={selectedParentNode?.id ?? "__root__"}
								/>
							</div>
						)}

						<ActionButton
							type="submit"
							icon="mdi/check"
							className={baseClass.element("submit-btn").toClassName()}
							onClick={submitConfig}
							label={t("editor", "Create integration")}
							style={ACTION_BUTTON_STYLE.PRIMARY}
							disabled={!selectedIntegration || !name || creating}
							isLoading={creating}
						/>
					</Form>
				</div>
			) : (
				<div className={baseClass.element("empty").toClassName()}>
					{t("editor", "Select integration type to connect your data.")}
				</div>
			)}
		</Panel>
	);
};
